import { useEffect, useState } from "react";

/**
 * Hold a fast-changing value still until it stops changing.
 *
 * Search boxes that feed a TanStack Query key fetch once per keystroke: the key
 * changes on every character, so "Fall 2026" is nine requests for one intent.
 * Debouncing the value rather than the request keeps the input itself
 * uncontrolled-feeling — the field updates immediately, only the query key lags.
 *
 * The timer is cleared on every change and on unmount, so a value that is still
 * moving never resolves and a component that leaves never sets state.
 */
export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
